import React, { Component } from 'react'
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { Box, Grid, SelectChangeEvent, Typography } from '@mui/material';
import { RouteComponentProps, RouterProps, generatePath } from 'react-router';
import CheckCircleOutlineTwoToneIcon from '@mui/icons-material/CheckCircleOutlineTwoTone';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { appContext } from '../../../../AppContext';
import IbssActionButton, { IActionButton } from '../../../../Components/Buttons/ActionButton/IbssActionButton';
import IbssDataGrid from '../../../../Components/Data/DataGrid/IbssDataGrid';
import IbssSvgIcon from '../../../../Components/Icons/SvgIcon/IbssSvgIcon';
import IbssDialog from '../../../../Components/Dialogs/BaseDialog/IbssDialog';
import IbssInputDropDown from '../../../../Components/Inputs/SelectList/IbssInputDropDown';
import IbssButtonRedo from '../../../../Components/Buttons/Button/IbssButton';
import IbssFilter from '../../../../Components/Forms/Filter/IbssFilter';
import Spinner from '../../../../Components/Navigation/LoadingSpinner/Spinner';
import { ISpaceCateringMenuItem } from '../../../../Providers.Api/CateringItems/GetManyEndpoint';
import { CateringItemsFilter, Classification, Status } from '../../../../Providers.Api/CateringItems/CateringItemRepository';
import { TypeHelper } from '../../../../Common/TypeHelper';
import Helper, { getAllBuildingsData } from '../../../../Common/Helper';
import { IUpdateSpaceCateringMenuItemPayload } from '../../../../Providers.Api/CateringItems/UpdateEndpoint';
import { ISupplierView } from '../../../../Providers.Api/CateringSuppliers/CateringSupplierRepository';
import IbssChip from "../../../../Components/Navigation/Chip/IbssChip";
import CreateIcon from '@mui/icons-material/Create';
import IbssAutocomplete from '../../../../Components/Inputs/AutoComplete/IbssAutocomplete';
import { TextField } from "@mui/material";
import { INode, PagedResponse } from '../../../../Providers.Api/Models';
import PreparingExportDialog from '../../../../Components/Dialogs/PreparingExportDialog/PreparingExportDialog';
import { QueryParams } from './QueryParams';
import { IbssPage } from '../../../../Components/Core/BasePage/IbssPage';
import { rights } from '../../../../Common/RightsManager';
import { Constants } from '../../../../Common/Constants';
import IbssPageHeader from '../../../../Components/Headings/PageHeading/IbssPageHeader';
import { CateringOrderPolicy } from './DataModels';
import { ICateringOrderPolicy } from '../../../../Providers.Api/CateringOrderPolicies/CateringOrderPoliciesRepository';
import { ApiConstants } from '../../../../Providers.Api/ApiConstants';
import ConfirmModal from '../../../../Components/Dialogs/ConfirmDialog/ConfirmModal';

export default class ListCateringItems extends IbssPage<IProps, IState, QueryParams>
{
    private get alert() { return appContext().alert; }
    private get labels() { return appContext().labels; }
    private get appState() { return appContext().state; }
    private get local() { return appContext().localStorageProvider; }
    private get apiClient() { return appContext().apiClient; }
    private pageCacheKey = "f47ac10b-58cc-4372-a567-0e02b2c3d479";

    constructor(props: IProps)
    {
        super(props, new QueryParams());
        this.state =
        {
            loading: false,
            taskList: [],
            isCreateRight: false,
            isCancelRight: false,
            isUpdateRight: false,
            buildingId: this.appState.buildingId,
            message: "",
            selectedTaskIds: [],
            actionType: "",
            searchTerm: "",
            openFilterModal: false,
            statusValue: "Any",
            classificationValue: "Any",
            supplierId: "",
            orderPolicyValue: "Any",
            disableBtn: true,
            enableBtn: true,
            filterStatusOptions: [],
            filterClassificationOptions: [],
            filterOrderPolicyOptions: [],
            suppliers: [],
            currency: '',
            showPreparingExportPopup: false,
            showDeletePopup: false,
            orderPolicies: [],
        }
    }

    public componentDidMount(): void
    {
        this.pageTitle = this.labels.HubLabelFacilityManagement;
    }

    public async queryParamsDidUpdate(firstLoad?: boolean, prevParams?: QueryParams): Promise<void>
    {
        const queryParams = this.queryParams;
        if (firstLoad)
        {
            appContext().inMemoryCache.clear(i => i.startsWith(this.pageCacheKey + '_'));
        }

        if (!queryParams.building)
        {
            return this.pushQueryParams({ building: this.appState.buildingId }, true);
        }
        else if (!queryParams.status)
        {
            return this.pushQueryParams({ status: 'Any' }, true);
        }
        else if (!queryParams.classification)
        {
            return this.pushQueryParams({ classification: 'Any' }, true);
        }
        else if (!queryParams.orderPolicy) 
        {
            return this.pushQueryParams({ orderPolicy: 'Any' }, true);
        }

        await this.setStateAsync({
            buildingId: queryParams.building ?? 1,
            statusValue: queryParams.status as Status,
            classificationValue: queryParams.classification as Classification,
            supplierId: queryParams.supplier ?? '',
            orderPolicyValue: queryParams.orderPolicy ?? 'Any',
            searchTerm: queryParams.filter ?? '',
        });

        this.setRights();
        await this.loadCateringOrderPolicy();
        await this.loadTasks();
        await this.loadSuppliers();
        this.setSelectedBuildingCurrency();
    }

    private setSelectedBuildingCurrency(): void
    {
        const buildings = getAllBuildingsData();
        let selectedBuilding: INode | null = null;
        for (let i = 0; i < buildings.length; i++)
        {
            if (buildings[i].Node_Id == this.state.buildingId)
            {
                selectedBuilding = buildings[i];
            }
        }
        if (selectedBuilding == null)
        {
            return;
        }
        this.setState({ currency: selectedBuilding.Cat_Crncy });
    }

    private setRights(): void 
    {
        this.setState({
            isCreateRight: rights.DATAMODEL.CateringMenuItems.Create.has(),
            isCancelRight: rights.DATAMODEL.CateringMenuItems.Delete.has(),
            isUpdateRight: rights.DATAMODEL.CateringMenuItems.Update.has(),
        });
    }

    private async loadTasks(forceRefresh?: boolean): Promise<void> 
    {
        try
        {
            this.setState({ loading: true });
            const endpoint = {
                name: 'apiClient.cateringItems.getMany',
                buildingId: this.state.buildingId,
                filter: new CateringItemsFilter({
                    classification: (this.state.classificationValue === "Any" ? null : this.state.classificationValue),
                    status: (this.state.statusValue === "Any" ? null : this.state.statusValue),
                    supplierId: (this.state.supplierId === "" ? null : this.state.supplierId),
                }),
            };

            if(forceRefresh)
            {
                appContext().inMemoryCache.clear(`${this.pageCacheKey}_${JSON.stringify(endpoint)}`);
            }

            const response = await appContext().inMemoryCache.lazyGetWithQuickExpiry(
                `${this.pageCacheKey}_${JSON.stringify(endpoint)}`,
                () => this.apiClient.cateringItems.getMany(this.state.buildingId, endpoint.filter),
            );

            if (!response) 
            {
                return;
            }

            let tasks = response.map(item => new MenuItemView(item));
            if (this.state.orderPolicyValue !== "Any")
            {
                tasks = tasks.filter(item => item.orderPolicyId === this.state.orderPolicyValue);
            }
            this.setState({ taskList: tasks, loading: false });
            this.tasksSelected(this.state.selectedTaskIds)
        }
        finally 
        {
            this.setState({ loading: false });
        }
    }

    private async loadCateringOrderPolicy(): Promise<void>
    {
        try
        {
            this.setState({ loading: true });

            const endpoint = {
                name: 'ibssApiClientV2.v2.byNodeid.cateringOrderPolicy.get',
                nodeId: this.state.buildingId,
            };

            const response = await appContext().inMemoryCache.lazyGetWithQuickExpiry(
                `${this.pageCacheKey}_${JSON.stringify(endpoint)}`,
                () => appContext().ibssApiClientV2.v2.byNodeid.cateringOrderPolicy.get<PagedResponse<CateringOrderPolicy[]>>({
                    nodeId: this.state.buildingId,
                    select: CateringOrderPolicy,
                    top: ApiConstants.largeODataTop,
                })
            );
            this.setState({ orderPolicies: response.value });
        }
        finally
        {
            this.setState({ loading: false });
        }
    }

    private getOrderPolicyName(menuItem: MenuItemView): String
    {
        const orderPolicyId = menuItem.orderPolicyId;
        if (!orderPolicyId) return "";

        const orderPolicy = this.state.orderPolicies.find(policy => policy.OrderPolicy_Id === orderPolicyId);
        return orderPolicy ? orderPolicy.Name : "";
    }

    private async loadSuppliers(): Promise<void> 
    {
        try
        {
            this.setState({ loading: true });
            const endpoint = {
                name: 'apiClient.cateringSuppliers.getCateringSupplierByNodeId',
                buildingId: this.state.buildingId,
            };
            const response = await appContext().inMemoryCache.lazyGetWithQuickExpiry(
                `${this.pageCacheKey}_${JSON.stringify(endpoint)}`,
                () => this.apiClient.cateringSuppliers.getCateringSupplierByNodeId(this.state.buildingId)
            );
            if (!response) 
            {
                return;
            }

            this.setState({
                suppliers: response.Value,
            })
        }
        finally 
        {
            this.setState({ loading: false });
        }
    }

    private filterTasks(searchTerm: string): MenuItemView[] 
    {
        const filteredTasks = this.state.taskList.filter(task =>
        {
            let key: keyof MenuItemView;
            for (key in task)
            {
                if (task[key]?.toString().includes(searchTerm))
                {
                    return true;
                }
            }
            return false;
        });
        return filteredTasks;
    }

    private async taskStatusUpdate(status: Status): Promise<void>
    {
        try 
        {
            const filtered = this.filterTasks(this.state.searchTerm).filter((item) => this.state.selectedTaskIds.includes(item.id));
            for (const item of filtered) 
            {
                await this.apiClient.cateringItems.update(this.state.buildingId, item.id,
                    {
                        Status: status,
                        ConcurrencyStamp: item.concurrencyStamp,
                    });
            }
            this.loadTasks();
        }
        catch (error) 
        {
        }
    }

    private debounceFilterTextTimer: NodeJS.Timeout | null = null;
    private handleSearchTermChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void
    {
        const searchTerm = event.target.value;

        this.setState({ searchTerm: searchTerm });
        clearTimeout(this.debounceFilterTextTimer ?? undefined);
        this.debounceFilterTextTimer = setTimeout(
            () => this.pushQueryParams({ filter: searchTerm === '' ? undefined : searchTerm }),
            Constants.longDebounceTimeInMilliseconds,
        );
    }

    private enabledClicked(): void 
    {
        this.taskStatusUpdate("StatusActive");
    }

    private disabledClicked(): void 
    {
        this.taskStatusUpdate("StatusInactive");
    }

    private async deleteClicked(): Promise<void>
    {
        try
        {
            for (const id of this.state.selectedTaskIds)
            {
                await this.apiClient.cateringItems.delete(this.state.buildingId, String(id));
            }
            // on deletion force the page to load fresh data instead of relying on cached data.
            this.loadTasks(true);
        }
        finally
        {
            this.setState({ showDeletePopup: false });
        }
    }

    private showDeletePopup(show: boolean ): void
    {
        this.setState({ showDeletePopup: show});
    }

    private filterModalCancelled(): void
    {
        this.setState({ openFilterModal: !this.state.openFilterModal });
        this.setState({ loading: false });
    }

    private async filterModalSubmitted(): Promise<void>
    {
        this.setState({ openFilterModal: false })

        this.pushQueryParams({
            status: this.state.statusValue,
            classification: this.state.classificationValue,
            orderPolicy: this.state.orderPolicyValue,
            supplier: this.state.supplierId === '' ? undefined : this.state.supplierId,
        });
    }

    private async filterButtonClicked(): Promise<void>
    {
        const status = [
            {
                label: this.labels.HubLabelAny,
                value: "Any"
            },
            {
                label: this.labels.HubLabelAvailable,
                value: "StatusActive"
            },
            {
                label: this.labels.HubLabelUnavailable,
                value: "StatusInactive"
            }
        ]
        const orderPolicyOptions = [
            {
                label: this.labels.HubLabelAny,
                value: "Any"
            },
            ...this.state.orderPolicies.map(policy => ({
                label: policy.Name,
                value: policy.OrderPolicy_Id
            }))
        ];
        const classifications = [
            {
                label: this.labels.HubLabelAny,
                value: "Any"
            },
            {
                label: this.labels.HubLabelBeverage,
                value: "MenuItemClassificationBeverage"
            },
            {
                label: this.labels.HubLabelSnack,
                value: "MenuItemClassificationSnack"
            },
            {
                label: this.labels.HubLabelFood,
                value: "MenuItemClassificationFood"
            },

        ]
        this.setState({
            openFilterModal: true,
            filterStatusOptions: status,
            filterClassificationOptions: classifications,
            filterOrderPolicyOptions: orderPolicyOptions,
        })

    }

    private tasksSelected(selection: GridRowSelectionModel): void
    {
        this.setState({ selectedTaskIds: selection });
        const selectedMenuItems = this.state.taskList.filter(menuItem => selection.contains(menuItem.id));
        const enableBtnState = (selectedMenuItems.length ? selectedMenuItems.some(e => e.status === "StatusActive") : true);
        const disableBtnState = (selectedMenuItems.length ? selectedMenuItems.some(e => e.status === "StatusInactive") : true);

        if (!enableBtnState)
        {
            this.setState({ enableBtn: false });
        }
        else
        {
            this.setState({ enableBtn: true });
        }

        if (!disableBtnState)
        {
            this.setState({ disableBtn: false });
        }
        else
        {
            this.setState({ disableBtn: true });
        }
    }

    private renderCateringStatus(status: string): React.ReactNode
    {
        if (status === "StatusActive")
        {
            return <IbssChip label={this.labels.HubLabelAvailable} sx={{
                backgroundColor: 'var(--ui-success-pastel)',
                color: "var(ui-text)",
            }} />;
        }
        else
        {
            return <IbssChip label={this.labels.HubLabelUnavailable} sx={{
                backgroundColor: 'var(--ui-error)',
                color: "var(--ui-light-text)",
            }} />;
        }
    }

    private redirectActionButton(cateringId: string): React.ReactNode
    {
        const { history } = this.props;
        return (
            <>
                {
                    this.state.isUpdateRight ?
                        <IbssSvgIcon className="pointer" onClick={() => history.push(`/operational-services-catering/${this.state.buildingId}/catering/${cateringId}`)}>
                            <CreateIcon />
                        </IbssSvgIcon>
                        : <p>-</p>
                }
            </>
        )
    }

    private supplierName(suppliers: MenuItemView): React.ReactNode 
    {
        const supplierId = suppliers.supplierId;
        const data = this.state.suppliers.filter((item) => item.Supplier_Id === supplierId)
        return data[0]?.Name
    }

    private exportClicked(): void
    {
        this.setState({ showPreparingExportPopup: true });

        let filterValues = [];

        if (this.state.classificationValue != "Any")
        {
            filterValues.push(`Classification eq '${this.state.classificationValue}'`)
        }
        if (this.state.statusValue != "Any")
        {
            filterValues.push(`Status eq '${this.state.statusValue}'`)
        }
        if (this.state.supplierId != "" && this.state.supplierId != null)
        {
            filterValues.push(`Supplier_Id eq '${this.state.supplierId}'`)
        }
        if (this.state.orderPolicyValue != "Any")
        {
            filterValues.push(`OrderPolicy_Id eq '${this.state.orderPolicyValue}'`)
        }

        appContext().ibssApiClientV2.v2.byNodeid.cateringMenuitem.download.post({
            nodeId: this.state.buildingId,
            body: {
                Node_Id: this.state.buildingId,
                Filter: filterValues.length == 0 ? "" : filterValues.join(' and '),
            }
        });
    }

    private getClassification(classification: MenuItemView): React.ReactNode 
    {
        const classificationName = classification.classification;

        switch (classificationName)
        {
            case "MenuItemClassificationBeverage":
                return this.labels.HubLabelBeverage;

            case "MenuItemClassificationSnack":
                return this.labels.HubLabelSnack;

            case "MenuItemClassificationFood":
                return this.labels.HubLabelFood;

            default:
                return <p>-</p>;
        }
    }

    public render(): JSX.Element
    {
        const { history } = this.props;

        const dataGridHeader: GridColDef<MenuItemView>[] =
            [
                {
                    headerName: this.labels.HubLabelItemCode,
                    field: Helper.nameOf<MenuItemView>("name"),
                    minWidth: 150,
                    flex: 1,
                },
                {
                    headerName: this.labels.HubLabelDescription,
                    field: Helper.nameOf<MenuItemView>("description"),
                    minWidth: 150,
                    flex: 1,
                },
                {
                    headerName: this.labels.HubLabelCost,
                    field: Helper.nameOf<MenuItemView>("unitPrice"),
                    minWidth: 150,
                    flex: 1,
                    renderCell: (params) => `${this.state.currency}${params.row.unitPrice}`
                },
                {
                    headerName: this.labels.HubLabelMenuOrderPolicy,
                    field: Helper.nameOf<MenuItemView>("orderPolicyId"),
                    minWidth: 150,
                    flex: 1,
                    renderCell: (params) => this.getOrderPolicyName(params.row)
                },
                {
                    headerName: this.labels.HubLabelSupplier,
                    field: "",
                    minWidth: 150,
                    flex: 1,
                    renderCell: (params) => this.supplierName(params.row)
                },
                {
                    headerName: this.labels.dataNotificationClassification_S,
                    field: Helper.nameOf<MenuItemView>("classification"),
                    minWidth: 150,
                    flex: 1,
                    renderCell: (params) => this.getClassification(params.row)
                },
                {
                    headerName: this.labels.HubLabelStatus,
                    field: Helper.nameOf<MenuItemView>("status"),
                    minWidth: 150,
                    flex: 1,
                    renderCell: (params) => this.renderCateringStatus(params.row.status)
                },
                {
                    headerName: this.labels.HubLabelAction,
                    field: Helper.nameOf<MenuItemView>("concurrencyStamp"),
                    flex: 1,
                    filterable: false,
                    sortable: false,
                    renderCell: (params) => <>{this.redirectActionButton(params.row.id)}</>
                }
            ];


        // Action buttons
        const actionButtons: IActionButton[] =
            [
                {
                    label: this.labels.HubButtonDelete,
                    icon: (
                        <IbssSvgIcon>
                            <DeleteOutlinedIcon />
                        </IbssSvgIcon>
                    ),
                    color: "error",
                    onClick: () => this.showDeletePopup(true),
                    disabled: this.state.selectedTaskIds.length > 0 && this.state.isCancelRight ? false : true
                },
                {
                    label: this.labels.HubLabelDisable,
                    icon: (
                        <IbssSvgIcon>
                            <CancelOutlinedIcon />
                        </IbssSvgIcon>
                    ),
                    color: "warning",
                    onClick: () => this.disabledClicked(),
                    disabled: !this.state.disableBtn && this.state.isUpdateRight ? false : true
                },
                {
                    label: this.labels.HubLabelEnable,
                    icon: (
                        <IbssSvgIcon>
                            <CheckCircleOutlineTwoToneIcon />
                        </IbssSvgIcon>
                    ),
                    color: "info",
                    onClick: () => this.enabledClicked(),
                    disabled: !this.state.enableBtn && this.state.isUpdateRight ? false : true
                },
                {
                    label: this.labels.HubButtonAdd,
                    icon: (
                        <IbssSvgIcon>
                            <AddIcon />
                        </IbssSvgIcon>
                    ),
                    color: "primary",
                    onClick: () => { history.push(`/operational-services-catering/${this.state.buildingId}/catering/0`) },
                    disabled: !this.state.isCreateRight
                }
            ];

        const filteredTasks = this.filterTasks(this.state.searchTerm);

        return (
            <>
                <div>
                    <PreparingExportDialog open={this.state.showPreparingExportPopup} onClose={() => this.setState({ showPreparingExportPopup: false })} />
                    {/* Filter Modal */}
                    <IbssDialog
                        open={this.state.openFilterModal}
                        onClose={() => this.filterModalCancelled()}
                        fullWidth
                        header={this.labels.HubLabelFilter}
                        dialogContent=
                        {
                            <>
                                <Typography className="mb-3">{this.labels.HubCateringFilterSubTitle}</Typography>
                                <div className="row my-3">
                                    <IbssInputDropDown
                                        options={this.state.filterStatusOptions}
                                        id="statusSelection"
                                        value={this.state.statusValue}
                                        inputLabel={this.labels.HubLabelStatus}
                                        fullWidth
                                        onChange={(event: SelectChangeEvent<Status>) => { this.setState({ statusValue: event.target.value as Status }) }}
                                    />
                                </div>
                                <div className="row my-3">
                                    <IbssInputDropDown
                                        options={this.state.filterClassificationOptions}
                                        id="statusSelection"
                                        value={this.state.classificationValue}
                                        inputLabel={this.labels.dataNotificationClassification_S}
                                        fullWidth
                                        onChange={(event: SelectChangeEvent<Classification>) => { this.setState({ classificationValue: event.target.value as Classification }) }}
                                    />
                                </div>
                                <div className="row my-3">
                                    <IbssInputDropDown
                                        options={this.state.filterOrderPolicyOptions}
                                        id="orderPolicySelection"
                                        value={this.state.orderPolicyValue}
                                        inputLabel={this.labels.HubLabelMenuOrderPolicy}
                                        fullWidth
                                        onChange={(event: SelectChangeEvent<string>) => { this.setState({ orderPolicyValue: event.target.value }) }}
                                    />
                                </div>
                                <div className="row my-3">
                                    <IbssAutocomplete
                                        fullWidth
                                        id="combo-box-demo"
                                        size="small"
                                        options={this.state.suppliers}
                                        getOptionLabel={(option) => option.Name}
                                        value={this.state.suppliers.find(supplier => supplier.Supplier_Id === this.state.supplierId)}
                                        renderOption={(props, option) => (
                                            <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                                                {option.Name}
                                            </Box>
                                        )}
                                        onChange={(event, value) =>
                                        {
                                            this.setState({
                                                supplierId: value ? value.Supplier_Id : null,
                                            });
                                        }}
                                        renderInput={(params) => <TextField {...params} label={this.labels.HubLabelSupplier} />}
                                    />
                                </div>
                            </>
                        }
                        footer=
                        {
                            <Box gap={2} display={'flex'}>
                                <IbssButtonRedo
                                    onClick={() => this.filterModalCancelled()}
                                    color="secondary"
                                    variant="outlined"
                                >
                                    {this.labels.HubButtonCancel}
                                </IbssButtonRedo>
                                <IbssButtonRedo
                                    color="primary"
                                    variant="contained"
                                    size="medium"
                                    onClick={() => this.filterModalSubmitted()}
                                >
                                    {this.labels.HubLabelOk}
                                </IbssButtonRedo>
                            </Box>
                        }
                    />
                    <ConfirmModal 
                        show={this.state.showDeletePopup}
                        modalHeading={this.labels.HubLabelConfirmation}
                        modalMessage={this.labels.OneLens_CateringItemsList_DeletePopupMessage}
                        confirmButtonText={this.labels.HubButtonDelete}
                        handleModal={() => this.showDeletePopup(false)}
                        okButton={() => this.deleteClicked()}
                    />
                    <div className="rightPanel">
                        {this.state.loading && <Spinner />}
                        <div className="rightPanel-main-content">
                            <div className="table-panel">
                                <Grid container rowSpacing={0} sx={{ display: 'flex', alignItems: 'center', mt: 0, ml: 0, pt: 1 }}>
                                    <Grid item md={12} >
                                        <IbssPageHeader
                                            title={this.labels.HubMenuCateringItems}
                                        />
                                    </Grid>
                                    <Grid item md={5} mt={2}>
                                        <IbssFilter
                                            searchTerm={this.state.searchTerm}
                                            onSearchTermChange={(event) => this.handleSearchTermChange(event)}
                                            onFilterButtonClick={() => this.filterButtonClicked()}
                                        />
                                    </Grid>
                                    <Grid item md={7} mt={2}>
                                        <Box component="div" sx={{ display: 'flex', justifyContent: 'right', alignItems: 'center', my: 1, mr: 0 }}>
                                            <IbssButtonRedo variant="contained" color="secondary" onClick={() => this.exportClicked()}>{this.labels.HubButtonExport}</IbssButtonRedo>
                                        </Box>
                                    </Grid>
                                    <Grid item md={12}>
                                        <Box component="div" sx={{ display: 'flex', justifyContent: 'right', alignItems: 'center', my: 1, mr: 0 }}>
                                            <IbssActionButton buttons={actionButtons} />
                                        </Box>
                                    </Grid>
                                </Grid>
                                <Box>
                                    <IbssDataGrid
                                        checkboxSelection
                                        columns={dataGridHeader}
                                        disableRowSelectionOnClick
                                        rows={filteredTasks}
                                        onRowSelectionModelChange={(rowSelectionModel: GridRowSelectionModel) => this.tasksSelected(rowSelectionModel)}
                                        initialState={{
                                            pagination: { paginationModel: { pageSize: 25 } },
                                        }}
                                        pageSizeOptions={[25, 50, 100]}
                                    />
                                </Box>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        )
    }

}

interface IProps extends RouterProps, RouteComponentProps
{
}


interface IState
{
    loading: boolean;
    taskList: MenuItemView[];
    isCreateRight: boolean;
    isCancelRight: boolean;
    isUpdateRight: boolean;
    buildingId: number;
    message: string;
    selectedTaskIds: GridRowSelectionModel;
    actionType: string;
    searchTerm: string;
    openFilterModal: boolean;
    statusValue: Status | "Any";
    classificationValue: Classification | "Any";
    supplierId: string;
    disableBtn: boolean;
    enableBtn: boolean;
    filterStatusOptions: IFilterOption[];
    filterClassificationOptions: IFilterOption[];
    filterOrderPolicyOptions: IFilterOption[];
    suppliers: ISupplierView[];
    currency: string;
    showPreparingExportPopup: boolean;
    showDeletePopup: boolean;
    orderPolicies: ICateringOrderPolicy[],
    orderPolicyValue: string
}

interface IFilterOption
{
    label: string;
    value: string;
}

class MenuItemView
{
    public id: string;
    public description: string;
    public unitPrice: number;
    public name: string;
    public supplierId: string;
    public status: Status;
    public classification: Classification;
    public concurrencyStamp: string;
    public orderPolicyId: string;

    constructor(value: ISpaceCateringMenuItem)
    {
        this.id = value.MenuItem_Id;
        this.name = value.Name;
        this.description = value.Description;
        this.unitPrice = value.UnitPrice;
        this.supplierId = value.Supplier_Id;
        this.status = value.Status;
        this.classification = value.Classification;
        this.concurrencyStamp = value.ConcurrencyStamp;
        this.orderPolicyId = value.OrderPolicy_Id
    }
}